/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/no-use-before-define */
import React, {
  useState, useEffect, useRef,
} from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  Popover,
  TextField,
} from '@mui/material';
import {
  useAuth,
} from '@panna/auth';
import { useStatuses } from 'src/hooks/query/statuses';
import { useMailTemplates } from 'src/hooks/query/mail-templates';
import { Loader } from 'src/components';
import PlacedWithMroadsForm from 'src/pages/JobRequisition/components/PlacedWithMroadsPopup';
import API from 'src/network';
import PannaInterview, { PannaInterviewFormValues } from 'src/pages/JobRequisition/components/PannaInterviewPopup';
import MLiveInterview, { MLiveFormValues } from 'src/pages/JobRequisition/components/MLiveInterviewPopup';
import { confirmWrapper } from '../Dialog';
import Mailer from './Mailer';
import {
  StatusDropdownProps,
  MailTemplateRequest,
  Option,
  CandidateMailStatus,
  CandidateActiveStatus,
} from './types';
import ScheduleClientInterview, { ScheduleClientInterviewFormValues } from './ScheduleClientInterview';
import Comments from './Comments';
import AuthorizationAndRTR from './AuthorizationAndRTR';
import RequestGatePass, { RequestGatePassFormValues } from './RequestGatePass';

const getMailDraftStorageKey = (
  jobOrderId: number | undefined,
  candidateId: number,
  status: string,
) => `ats-mail-draft-${jobOrderId}-${candidateId}-${status}`;

const loadMailDraft = <T extends unknown>(key: string): Partial<T> | null => {
  try {
    const storedValue = sessionStorage.getItem(key);
    return storedValue ? JSON.parse(storedValue) as Partial<T> : null;
  } catch {
    return null;
  }
};

const saveMailDraft = (key: string, data: object) => {
  sessionStorage.setItem(key, JSON.stringify(data));
};

const clearMailDraft = (key: string) => {
  sessionStorage.removeItem(key);
};

const mailTemplateList = [
  CandidateMailStatus.RATE_CONFIRMATION_WITH_CONSULTANCY,
  CandidateMailStatus.RTR_FORM_WITH_CANDIDATE,
  CandidateMailStatus.CANDIDATE_REQUESTED_JOBDESCRIPTION,
  CandidateMailStatus.AUTHORIZATION_FORM,
  CandidateMailStatus.ON_VOICEMAIL_1,
  CandidateMailStatus.ON_VOICEMAIL_2,
  CandidateMailStatus.ON_VOICEMAIL_3,
  CandidateMailStatus.ON_VOICEMAIL_Final,
  CandidateMailStatus.UPDATE_INTERVIEW,
  CandidateMailStatus.CANCEL_INTERVIEW,
  CandidateMailStatus.REMINDER_INTERVIEW,
  CandidateMailStatus.FACE_2_FACE,
  CandidateMailStatus.ONBOARDING_GATE_PASS,
  CandidateMailStatus.REJECTED,
];

const undoAbleStatusList = [
  CandidateActiveStatus.FOLLOW_UP,
  CandidateActiveStatus.NOT_REACHABLE,
  CandidateActiveStatus.SUBMITTED_TO_CLIENT,
  CandidateActiveStatus.COMPLETED_CLIENT_INTERVIEW,
  CandidateActiveStatus.REQUIREMENT_CLOSED,
  CandidateActiveStatus.NOT_SUITABLE_FOR_POSITION,
  CandidateActiveStatus.NOT_INTERESTED_TO_RELOCATE,
  CandidateActiveStatus.FAKE_CANDIDATE,
  CandidateActiveStatus.CANDIDATE_ALREADY_SUBMITED_BY_OTHER_VENDOR,
  CandidateActiveStatus.LOOKING_FOR_HIGHER_RATE,
  CandidateActiveStatus.SUBMITTED_TO_CLIENT_BY_OTHER_VENDOR,
  CandidateActiveStatus.NOT_LOOKING_FOR_CHANGE,
  CandidateActiveStatus.NO_CONTACT_INFO,
  CandidateActiveStatus.DISCUSSING,
  CandidateActiveStatus.REJECTED_BY_CLIENT,
  CandidateActiveStatus.RESUME_SUBMITTED_FORREVIEW,
  CandidateActiveStatus.ASSIGNMENT_STARTED,
  CandidateActiveStatus.ASSIGNMENT_COMPLETED,
  CandidateActiveStatus.CLEARED_FOR_NEXT_STEPS,
  CandidateActiveStatus.CLAIMED,
  CandidateActiveStatus.LOOKING_C2C_ONLY,
];

const scheduleInterviewList = [
  CandidateActiveStatus.SCHEDULE_PANNA_INTERVIEW,
  CandidateActiveStatus.SCHEDULE_MLIVE_INTERVIEW,
  CandidateActiveStatus.PHONE_CALL,
  CandidateActiveStatus.ADD_COMMENT,
  CandidateActiveStatus.DO_NOT_HIRE,
];

const scheduleClientInterview = [
  CandidateMailStatus.SCHEDULE_CLIENT_INTERVIEW,
];

const rtrandAuthorizationForm = [
  CandidateMailStatus.AUTHORIZATION_FORM,
  CandidateMailStatus.RTR_FORM_WITH_CANDIDATE,
];

const StatusDropdown = function StatusDropdown(props: StatusDropdownProps) {
  const [tempStatus, setTempStatus] = useState<Option | undefined>(undefined);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [openMailDialog, setOpenMainDialog] = React.useState<boolean>(false);
  const [template, setTemplate] = useState<any>('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [undoAnchorEl, setUndoAnchorEl] = React.useState<HTMLElement | null>(null);
  const anchorTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const [openPopUpDialog, setOpenPopUpDialog] = React.useState<boolean>(false);
  const [loadingPopUpDialog, setLoadingPopUpDialog] = useState(false);
  const [
    openScheduleClientInterviewDialog, setOpenScheduleClientInterviewDialog,
  ] = useState(false);

  const [payTypes, setPayTypes] = useState([]);
  const [workAuthorizations, setWorkAuthorizations] = useState([]);
  const [resumeSources, setResumeSources] = useState([]);
  const [preferredLocations, setPreferredLocations] = useState([]);
  const autoCompleteRef = useRef(null);
  const {
    candidateId,
    candidateStatus,
    jobOrderId,
    emailIds,
    candidateName,
    onSuccess,
  } = props;

  const {
    statusesOptions,
  } = useStatuses();

  const {
    mailTemplateOptions,
  } = useMailTemplates();

  const { authState } = useAuth();
  const { userInfo } = authState || {};
  const { emailAddress: ccMailAddress } = userInfo || {};

  const getStatus = (value: string) => ({
    label: value.split('_').join(' '),
    value,
  });

  const getMailTemplate = async (request: MailTemplateRequest) => {
    const status = tempStatus && tempStatus.value;
    setLoadingTemplates(true);
    try {
      const mailTemplate = await API.getInstance().getMailTemplate(request);
      if (rtrandAuthorizationForm.includes(status as CandidateMailStatus)) {
        await getAllPayTypes();
        await getAllWorkAuthorizations();
      }
      setTemplate(mailTemplate);
      setOpenMainDialog(true);
    } catch (e) {
      await confirmWrapper('Something went wrong', {
        hideSecondaryBtn: true,
        variant: 'danger',
      });
    } finally {
      setLoadingTemplates(false);
    }
  };

  const getScheduleClientInterviewMailTemplate = async (request: MailTemplateRequest) => {
    try {
      const mailTemplate = await API.getInstance().getMailTemplate(request);
      setTemplate(mailTemplate);
    } catch (e) {
      await confirmWrapper('Something went wrong', {
        hideSecondaryBtn: true,
        variant: 'danger',
      });
    }
  };

  const updateCandidateStatus = async (request: any) => {
    setUpdatingStatus(true);
    try {
      await API.getInstance().updateCandidateStatus(request);
      if (onSuccess) onSuccess();
    } catch (e) {
      await confirmWrapper('Something went wrong', {
        hideSecondaryBtn: true,
        variant: 'danger',
      });
      setTempStatus(undefined);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const updatePlacedWithMroads = async (request: any) => {
    const status = tempStatus && tempStatus.value;
    const updateStatusRequest = {
      candidateId,
      jobOrderId,
      status,
    };

    setTempStatus(undefined);
    setUpdatingStatus(true);
    try {
      await API.getInstance().updatePlacedWithMroads(request);
      await confirmWrapper(`${status} successfully`, {
        hideSecondaryBtn: true,
        hidePrimaryBtn: true,
        variant: 'success',
        autoClose: true,
      });
      updateCandidateStatus(updateStatusRequest);
    } catch (e) {
      await confirmWrapper('Something went wrong', {
        hideSecondaryBtn: true,
        variant: 'danger',
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const schedulePannaInterview = async (data: PannaInterviewFormValues) => {
    const { expiry, interviewPositionId } = data;
    const status = tempStatus && tempStatus.value;
    const request = {
      jobOrderId: jobOrderId as number,
      candidateId,
      expiry,
      interviewPositionId,
    };

    const updateStatusRequest = {
      candidateId,
      jobOrderId,
      status,
    };

    try {
      setLoadingPopUpDialog(true);
      await API.getInstance().schedulePanna(request);
      const message = 'Panna interview scheduled successfully!';
      await confirmWrapper(message, {
        hideSecondaryBtn: true,
        hidePrimaryBtn: true,
        variant: 'success',
        autoClose: true,
      });
      setOpenPopUpDialog(false);
      updateCandidateStatus(updateStatusRequest);
    } catch (err) {
      await confirmWrapper('Something went wrong', {
        hideSecondaryBtn: true,
        variant: 'danger',
      });
    } finally {
      setLoadingPopUpDialog(false);
      setTempStatus(undefined);
    }
  };

  const scheduleLiveInterview = async (data: MLiveFormValues) => {
    const { interviewerSlotId } = data;
    const status = tempStatus && tempStatus.value;

    const reqData = {
      interviewerSlotId,
      jobOrderId: jobOrderId as number,
      candidateId,
    };

    const updateStatusRequest = {
      candidateId,
      jobOrderId,
      status,
    };

    try {
      setLoadingPopUpDialog(true);
      await API.getInstance().scheduleLive(reqData);
      const message = 'MLive interview scheduled successfully!';
      await confirmWrapper(message, {
        hideSecondaryBtn: true,
        hidePrimaryBtn: true,
        variant: 'success',
        autoClose: true,
      });
      setOpenPopUpDialog(false);
      updateCandidateStatus(updateStatusRequest);
    } catch (err) {
      await confirmWrapper('Something went wrong', {
        hideSecondaryBtn: true,
        variant: 'danger',
      });
    } finally {
      setLoadingPopUpDialog(false);
      setTempStatus(undefined);
    }
  };

  const updateCandidateComments = async (data: any) => {
    const { comments } = data;
    const status = tempStatus && tempStatus.value;

    const reqData = {
      jobOrderId: jobOrderId as number,
      candidateId,
      comments,
    };

    const updateStatusRequest = {
      candidateId,
      jobOrderId,
      status,
    };

    try {
      setLoadingPopUpDialog(true);
      await API.getInstance().updateStatusComments(reqData);
      const message = 'Comments added successfully!';
      await confirmWrapper(message, {
        hideSecondaryBtn: true,
        hidePrimaryBtn: true,
        variant: 'success',
        autoClose: true,
      });
      onSuccess?.();
      setOpenPopUpDialog(false);
      if (status !== CandidateActiveStatus.ADD_COMMENT) {
        updateCandidateStatus(updateStatusRequest);
      }
    } catch (err) {
      await confirmWrapper('Something went wrong', {
        hideSecondaryBtn: true,
        variant: 'danger',
      });
    } finally {
      setLoadingPopUpDialog(false);
      setTempStatus(undefined);
    }
  };

  useEffect(() => {
    const status = tempStatus && tempStatus.value;
    if (status && mailTemplateList.includes(status as CandidateMailStatus)) {
      getMailTemplate({
        mailTemplate: status,
        jobOrderId: Number(jobOrderId),
        candidateId,
      });
    } else if (status && scheduleInterviewList.includes(status as CandidateActiveStatus)) {
      setOpenPopUpDialog(true);
    } else if (status && scheduleClientInterview.includes(status as CandidateMailStatus)) {
      setOpenScheduleClientInterviewDialog(true);
    }
  }, [tempStatus]);

  useEffect(() => {
    const status = tempStatus && tempStatus.value;
    if (status && undoAbleStatusList.includes(status as any)) {
      if (autoCompleteRef && autoCompleteRef.current) {
        setUndoAnchorEl(autoCompleteRef.current);
      }

      anchorTimerRef.current = setTimeout(() => {
        setUndoAnchorEl(null);

        const updateStatusRequest = {
          candidateId,
          jobOrderId,
          status,
        };
        updateCandidateStatus(updateStatusRequest);
      }, 3000);
      return () => clearTimeout(anchorTimerRef.current);
    }
  }, [tempStatus]);

  useEffect(() => {
    const status = tempStatus && tempStatus.value;
    if (status?.includes('PLACED WITH')) {
      if (autoCompleteRef && autoCompleteRef.current) {
        setUndoAnchorEl(autoCompleteRef.current);
      }
    }
  }, [tempStatus]);

  const onCancel = () => {
    setTempStatus(undefined);
    setOpenMainDialog(false);
    setOpenScheduleClientInterviewDialog(false);
    setUndoAnchorEl(null);
  };

  const getDraftKey = (status: string) => getMailDraftStorageKey(jobOrderId, candidateId, status);

  const handleDraftChange = (status: string, data: object) => {
    saveMailDraft(getDraftKey(status), data);
  };

  const clearDraftForStatus = (status: string) => {
    clearMailDraft(getDraftKey(status));
  };

  // closing Dialog popup - schedule panna and mlive popups
  const onClosePopUp = () => {
    setTempStatus(undefined);
    setOpenPopUpDialog(false);
  };

  const sendEmail = async (mailData: any) => {
    const {
      content,
      subject,
      to,
      cc,
      otherInterviewers,
      how,
      date,
      time,
      timeZone,
      duration,
      who,
      where,
      currency,
      payRate,
      payType,
      visaStatus,
      interviewerId,
    } = mailData;

    const mailTemplate = how || (tempStatus && tempStatus.value) || '';
    const { hashKey } = template;

    const request = {
      mailSubject: subject,
      mailBody: content,
      emailTagsList: to,
      emailTagsCcList: mailTemplate !== CandidateMailStatus.REJECTED ? cc : '',
      otherInterviewers,
      candidateId,
      jobOrderId,
      mailTemplate,
      candidateName,
      ...(hashKey ? { hashKey } : {}),
      ...(how ? { clientInterviewMode: how } : {}),
      ...(date ? { interviewDate: date } : {}),
      ...(time ? { interviewTime: time } : {}),
      ...(date ? { startDate: date } : {}),
      ...(time ? { startTime: time } : {}),
      ...(timeZone ? { timeZone } : {}),
      ...(duration ? { durationTime: duration } : {}),
      ...(who ? { interviewerName: who } : {}),
      ...(where ? { clientInterviewLocation: where } : {}),
      ...(currency ? { currency } : {}),
      ...(payRate ? { payRate } : {}),
      ...(payType ? { payType } : {}),
      ...(visaStatus ? { visaStatus } : {}),
      ...(interviewerId ? { interviewerId } : {}),
    };

    try {
      await API.getInstance().sendEmail(request);
      await confirmWrapper('Email sent successfully', {
        hideSecondaryBtn: true,
        hidePrimaryBtn: true,
        variant: 'success',
        autoClose: true,
      });
      if (onSuccess) {
        await onSuccess();
      }
      const sentStatus = how || (tempStatus && tempStatus.value) || '';
      if (sentStatus) {
        clearDraftForStatus(sentStatus);
      }
      setOpenMainDialog(false);
      setOpenScheduleClientInterviewDialog(false);
    } catch (err) {
      await confirmWrapper('Something went wrong', {
        hideSecondaryBtn: true,
        variant: 'danger',
      });
      setTempStatus(undefined);
    }
  };

  const getAllPayTypes = async () => {
    try {
      const payTypesResponse: any = await API.getInstance().getAllPaytypes();
      setPayTypes(payTypesResponse);
    } catch (e) {
      console.log(e);
    }
  };

  const getAllWorkAuthorizations = async () => {
    try {
      const workAuthorizationsResponse: any = await API.getInstance().getAllWorkAuthorizations();
      setWorkAuthorizations(workAuthorizationsResponse);
    } catch (e) {
      console.log(e);
    }
  };

  const getAllResumeSources = async () => {
    try {
      const resumeSourcesRes: any = await API.getInstance().getAllResumeSources();
      setResumeSources(resumeSourcesRes);
    } catch (e) {
      console.log(e);
    }
  };

  const getPreferredJobLocations = async () => {
    try {
      const preferredLocationsResponse: any = await API.getInstance()
        .getPreferredJobLocations(jobOrderId);
      setPreferredLocations(preferredLocationsResponse.data);
    } catch (e) {
      console.log(e);
    }
  };

  const prepareSaveRequestObj = (data: any) => {
    const request = {
      firstName: data?.firstName,
      lastName: data?.lastName,
      middleName: '',
      phoneNumber: data?.phoneNumber,
      emailAddress: data?.emailAddress,
      candidateLocation: data?.candidateLocation,
      preferredJobLocation: data?.preferredJobLocation,
      dateOfBirth: data?.dateOfBirth,
      experienceLevel: data?.experienceLevel,
      candidateTitle: data?.candidateTitle,
      payType: data?.payType,
      payRate: data?.payRate,
      consultancyName: data?.consultancyName,
      consultancyContactPerson: data?.consultancyContactPerson,
      consultancyPhoneNumber: data?.consultancyPhoneNumber,
      consultancyEmailAddress: data?.consultancyEmailAddress,
      consultancyId: data?.consultancyId,
      reLocation: data?.reLocation,
      visaStatus: data?.visaStatus,
      resumeSource: data?.resumeSource,
      socialLinks: data?.socialLinks,
      comments: data?.comments,
      candidateJobMappings: [{
        jobOrderId,
      }],
      candidateComments: [{
        jobOrderId,
        comments: data?.comments,
      }],
      candidateId,
      jobOrderId,
    };
    return request;
  };

  const getPopOverContent = () => {
    const { resumeData } = props;
    const status = tempStatus && tempStatus.value;
    if (!status?.includes('PLACED WITH')) {
      switch (status) {
        case CandidateActiveStatus.FOLLOW_UP:
        case CandidateActiveStatus.NOT_REACHABLE:
        case CandidateActiveStatus.SUBMITTED_TO_CLIENT:
        case CandidateActiveStatus.COMPLETED_CLIENT_INTERVIEW:
        case CandidateActiveStatus.REQUIREMENT_CLOSED:
        case CandidateActiveStatus.NOT_SUITABLE_FOR_POSITION:
        case CandidateActiveStatus.NOT_INTERESTED_TO_RELOCATE:
        case CandidateActiveStatus.FAKE_CANDIDATE:
        case CandidateActiveStatus.CANDIDATE_ALREADY_SUBMITED_BY_OTHER_VENDOR:
        case CandidateActiveStatus.LOOKING_FOR_HIGHER_RATE:
        case CandidateActiveStatus.SUBMITTED_TO_CLIENT_BY_OTHER_VENDOR:
        case CandidateActiveStatus.NOT_LOOKING_FOR_CHANGE:
        case CandidateActiveStatus.NO_CONTACT_INFO:
        case CandidateActiveStatus.DISCUSSING:
        case CandidateActiveStatus.REJECTED_BY_CLIENT:
        case CandidateActiveStatus.RESUME_SUBMITTED_FORREVIEW:
        case CandidateActiveStatus.ASSIGNMENT_STARTED:
        case CandidateActiveStatus.ASSIGNMENT_COMPLETED:
        case CandidateActiveStatus.CLEARED_FOR_NEXT_STEPS:
        case CandidateActiveStatus.CLAIMED:
        case CandidateActiveStatus.LOOKING_C2C_ONLY:
          return (
            <Box className="px-3 py-2 flex gap-3 items-center justify-center">
              Status Changed.
              <Button
                variant="text"
                onClick={() => {
                  if (anchorTimerRef.current) {
                    clearTimeout(anchorTimerRef.current);
                    setUndoAnchorEl(null);
                    setTempStatus(undefined);
                  }
                }}
              >
                Undo
              </Button>
            </Box>
          );
        default:
          return null;
      }
    } else {
      return (
        <PlacedWithMroadsForm
          status={status || ''}
          onSave={(savedata) => {
            const request = prepareSaveRequestObj(savedata);
            updatePlacedWithMroads(request);
          }}
          resumeInfo={resumeData}
          payTypes={payTypes}
          workAuthorizations={workAuthorizations}
          resumeSources={resumeSources}
          preferredLocations={preferredLocations}
          onCancel={onCancel}
        />
      );
    }
  };

  const getToEmailIds = (currentStatus: any) => {
    switch (currentStatus) {
      case CandidateMailStatus.RTR_FORM_WITH_CANDIDATE:
      case CandidateMailStatus.CANDIDATE_REQUESTED_JOBDESCRIPTION:
      case CandidateMailStatus.AUTHORIZATION_FORM:
      case CandidateMailStatus.ON_VOICEMAIL_1:
      case CandidateMailStatus.ON_VOICEMAIL_2:
      case CandidateMailStatus.ON_VOICEMAIL_3:
      case CandidateMailStatus.ON_VOICEMAIL_Final:
      case CandidateMailStatus.UPDATE_INTERVIEW:
      case CandidateMailStatus.CANCEL_INTERVIEW:
      case CandidateMailStatus.REMINDER_INTERVIEW:
      case CandidateMailStatus.FACE_2_FACE:
      case CandidateMailStatus.ONBOARDING_GATE_PASS:
      case CandidateMailStatus.REJECTED:
        return emailIds || [];

      case CandidateMailStatus.RATE_CONFIRMATION_WITH_CONSULTANCY:
        return [template.consultancyEmailAddress] || [];

      default:
        return [];
    }
  };

  const getDialogContent = () => {
    // const { resumeData } = props;
    const status = tempStatus && tempStatus.value;
    switch (status) {
      case CandidateMailStatus.RATE_CONFIRMATION_WITH_CONSULTANCY:
      case CandidateMailStatus.CANDIDATE_REQUESTED_JOBDESCRIPTION:
      case CandidateMailStatus.ON_VOICEMAIL_1:
      case CandidateMailStatus.ON_VOICEMAIL_2:
      case CandidateMailStatus.ON_VOICEMAIL_3:
      case CandidateMailStatus.ON_VOICEMAIL_Final:
      case CandidateMailStatus.UPDATE_INTERVIEW:
      case CandidateMailStatus.CANCEL_INTERVIEW:
      case CandidateMailStatus.REMINDER_INTERVIEW:
      case CandidateMailStatus.REJECTED:
        return (
          <Mailer
            defaultValues={{
              to: getToEmailIds(status),
              cc: [(ccMailAddress || '')],
              subject: template?.mailSubject,
              content: template?.mailContent,
            }}
            loading={false}
            onSubmit={sendEmail}
            onCancel={onCancel}
            title={tempStatus?.label}
          />
        );

      case CandidateMailStatus.RTR_FORM_WITH_CANDIDATE:
      case CandidateMailStatus.AUTHORIZATION_FORM:
        return (
          <AuthorizationAndRTR
            defaultValues={{
              to: getToEmailIds(status),
              cc: [(ccMailAddress || '')],
              subject: template?.mailSubject,
              content: template?.mailContent,
              currency: template?.currency,
              payRate: template?.payRate,
              payType: template?.payType,
              visaStatus: template?.visaStatus,
            }}
            payTypes={payTypes}
            workAuthorizations={workAuthorizations}
            loading={false}
            onSubmit={sendEmail}
            onCancel={onCancel}
            title={tempStatus?.label}
          />
        );

      case CandidateMailStatus.FACE_2_FACE:
      case CandidateMailStatus.ONBOARDING_GATE_PASS:
        return (
          <RequestGatePass
            defaultValues={{
              to: getToEmailIds(status),
              cc: [(ccMailAddress || '')],
              subject: template?.mailSubject,
              content: template?.mailContent,
            }}
            savedDraft={loadMailDraft<RequestGatePassFormValues>(getDraftKey(status)) || undefined}
            onDraftChange={(data) => handleDraftChange(status, data)}
            loading={false}
            onSubmit={sendEmail}
            onCancel={onCancel}
            title={tempStatus?.label}
          />
        );

      // case CandidateMailStatus.PLACED_WITH_MROADS:
      //   return (
      //     <PlacedWithMroadsForm
      //       onSave={(savedata) => {
      //         const request = prepareSaveRequestObj(savedata);
      //         updateCandidateStatus(request);
      //       }}
      //       resumeInfo={resumeData}
      //       payTypes={payTypes}
      //       jobTypes={jobTypes}
      //       locations={locations}
      //       workAuthorizations={workAuthorizations}
      //       resumeSources={resumeSources}
      //       preferredLocations={preferredLocations}
      //       onCancel={onCancel}
      //     />
      //   );
      default:
        return null;
    }
  };

  const getPopUpContent = () => {
    const status = tempStatus && tempStatus.value;
    switch (status) {
      case CandidateActiveStatus.SCHEDULE_PANNA_INTERVIEW:
        return (
          <PannaInterview
            onCancel={onClosePopUp}
            onSubmit={schedulePannaInterview}
            loading={loadingPopUpDialog}
          />
        );
      case CandidateActiveStatus.SCHEDULE_MLIVE_INTERVIEW:
        return (
          <MLiveInterview
            onCancel={onClosePopUp}
            onSubmit={scheduleLiveInterview}
            loading={loadingPopUpDialog}
          />
        );
      case CandidateActiveStatus.PHONE_CALL:
      case CandidateActiveStatus.ADD_COMMENT:
      case CandidateActiveStatus.DO_NOT_HIRE:
        return (
          <Comments
            onCancel={onClosePopUp}
            onSubmit={updateCandidateComments}
            title={tempStatus?.label}
          />
        );
      default:
        return null;
    }
  };

  const getScheduleClientInterviewPopup = () => {
    const status = tempStatus && tempStatus.value;
    switch (status) {
      case CandidateMailStatus.SCHEDULE_CLIENT_INTERVIEW:
        return (
          <ScheduleClientInterview
            defaultValues={{
              to: emailIds || [],
              cc: [(ccMailAddress || '')],
              subject: template?.mailSubject,
              content: template?.mailContent,
            }}
            savedDraft={
              loadMailDraft<ScheduleClientInterviewFormValues>(
                getDraftKey(CandidateMailStatus.SCHEDULE_CLIENT_INTERVIEW),
              ) || undefined
            }
            onDraftChange={(data) => handleDraftChange(
              CandidateMailStatus.SCHEDULE_CLIENT_INTERVIEW,
              data,
            )}
            onCancel={onCancel}
            getMailTemplate={getScheduleClientInterviewMailTemplate}
            jobOrderId={jobOrderId}
            candidateId={candidateId}
            onSubmit={sendEmail}
            loading={loadingPopUpDialog}
            title={tempStatus?.label}
          />
        );

      default:
        return null;
    }
  };

  const status = tempStatus && tempStatus.value;
  return (
    <div className="relative">
      <Autocomplete
        ref={autoCompleteRef}
        value={tempStatus || getStatus(candidateStatus)}
        disabled={
          candidateStatus.includes('PLACED WITH')
          || candidateStatus.includes('DO_NOT_HIRE')
        }
        disableClearable
        disablePortal
        options={[
          ...statusesOptions
            .map(({ label, value }: Option) => ({
              label: label.split('_').join(' '),
              value,
            })),
          ...mailTemplateOptions
            .map(({ label, value }: Option) => ({
              label: label.split('_').join(' '),
              value,
            })),
        ].sort((a: Option, b: Option) => a.label.localeCompare(b.label))}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        sx={{
          '& fieldset': { border: 'none' },
          '& .MuiInputLabel-root': { fontSize: 12 },
          '& .Mui-disabled': {
            cursor: 'not-allowed',
            pointerEvents: 'none',
          },
        }}
        slotProps={{
          paper: {
            sx: {
              '& .MuiAutocomplete-listbox': {
                '& .MuiAutocomplete-option': {
                  fontSize: 10,
                },
              },
            },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            inputProps={{ ...params.inputProps, style: { fontSize: 12 } }}
          />
        )}
        onChange={async (e, v: Option) => {
          setTempStatus(v);
          if (v && v.label.includes('PLACED WITH')) {
            await getAllPayTypes();
            await getAllWorkAuthorizations();
            await getAllResumeSources();
            await getPreferredJobLocations();
          }
        }}
      />
      {tempStatus !== undefined ? (
        <Dialog
          open={openMailDialog}
          maxWidth="md"
        >
          {getDialogContent()}
        </Dialog>
      ) : null}

      <Dialog
        open={openPopUpDialog}
        maxWidth="md"
      >
        {getPopUpContent()}
      </Dialog>

      {tempStatus !== undefined ? (
        <Dialog
          open={openScheduleClientInterviewDialog}
          maxWidth="md"
        >
          {getScheduleClientInterviewPopup()}
        </Dialog>
      ) : null}

      {tempStatus !== undefined ? (
        <div className="absolute gap-2 right-4 flex top-4 z-50">
          <Popover
            open={Boolean(undoAnchorEl)}
            anchorEl={undoAnchorEl}
            anchorReference={tempStatus.label.includes('PLACED WITH') ? 'none' : 'anchorEl'}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            PaperProps={status?.includes('PLACED WITH') ? {
              style: { width: '75%' },
            } : {}}
            // onClose={(e) => {
            //   if (e) {
            //     (e as Event).stopPropagation();
            //   }
            //   setUndoAnchorEl(null);
            // }}
            // onClick={(e) => e.stopPropagation()}
          >
            {getPopOverContent()}
          </Popover>
        </div>
      ) : null}

      {loadingTemplates && (
        <div className="fixed top-[50vh] left-[50vw]">
          <Loader />
        </div>
      )}

      {updatingStatus && (
        <div className="fixed top-[50vh] left-[50vw]">
          <Loader
            text="Updating Candidate Status..."
          />
        </div>
      )}
    </div>
  );
};

StatusDropdown.defaultProps = {
  jobOrderId: 0,
  emailIds: [],
};

StatusDropdown.propTypes = {

};

export default StatusDropdown;
